import {
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';
import {useCreateAuthenticatedFetch} from '../useCreateAuthenticatedFetch';
import baseUrl from '../baseUrl';
import {handleResponse} from '../handleResponse';

export type GettingStartedAudience = 'administrator' | 'maintainer' | 'member';

export interface GettingStartedStep {
    key: string;
    title: string;
    why: string;
    actionLabel: string;
    actionPath: string;
    docPath?: string | null;
    assistantPrompt: string;
    optional: boolean;
    done: boolean;
    count?: number | null;
    acknowledgeLabel?: string | null;
    acknowledged: boolean;
}

export interface GettingStarted {
    audience: GettingStartedAudience;
    dismissed: boolean;
    steps: GettingStartedStep[];
    requiredDone: number;
    requiredTotal: number;
    complete: boolean;
    workspaceEmpty: boolean;
    sampleDataLoaded: boolean;
}

/** Personal: hide the getting started card and navbar prompt. Mirrors GettingStartedCatalog.cs. */
export const HUB_DISMISSED = 'hub-dismissed';
/** Workspace-wide: the default working week is fine. */
export const WORKING_HOURS_ACKNOWLEDGED = 'working-hours';

// Under 'resource-allocation' so the assistant's planning-tool refresh
// (api/assistantToolRefresh.ts) refetches it after the assistant changes data.
export const GETTING_STARTED_QUERY_KEY = [
    'resource-allocation',
    'getting-started'
];

export const useGettingStarted = (
    enabled = true
): UseQueryResult<GettingStarted, Error> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();

    return useQuery({
        queryKey: GETTING_STARTED_QUERY_KEY,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/getting-started`
            );
            return handleResponse<GettingStarted>(response);
        },
        enabled
    });
};

interface AcknowledgeArgs {
    key: string;
    /** False takes the acknowledgement back (e.g. show the card again). */
    acknowledged: boolean;
}

export const useAcknowledgeGettingStarted = (): UseMutationResult<
    unknown,
    Error,
    AcknowledgeArgs
> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({key, acknowledged}: AcknowledgeArgs) => {
            const response = acknowledged
                ? await authenticatedFetch(
                      `${baseUrl}/api/getting-started/acknowledgements`,
                      {
                          method: 'POST',
                          headers: {'Content-Type': 'application/json'},
                          body: JSON.stringify({key})
                      }
                  )
                : await authenticatedFetch(
                      `${baseUrl}/api/getting-started/acknowledgements/${encodeURIComponent(key)}`,
                      {method: 'DELETE'}
                  );
            return handleResponse<unknown>(response);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: GETTING_STARTED_QUERY_KEY})
    });
};

export interface SampleDataStatus {
    loaded: boolean;
    loadedAt?: string | null;
    people: number;
    projects: number;
}

/**
 * Loads (load: true) or clears (load: false) the workspace's sample data. The
 * server tells every open page to reload the plan; this refreshes the queries.
 */
export const useSampleData = (): UseMutationResult<
    unknown,
    Error,
    {load: boolean}
> => {
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({load}: {load: boolean}) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/getting-started/sample-data`,
                {method: load ? 'POST' : 'DELETE'}
            );
            return handleResponse<unknown>(response);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: ['resource-allocation']})
    });
};
